import { createContext, useContext, useEffect, useState } from 'react'
import { supabase, isSupabaseConfigured, setRememberMe } from '../config/supabase'
import { queryClient } from '../config/queryClient'
import { trackSignOut } from '../config/analyticsEvents'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  // Helper to check if error indicates invalid/expired session
  const isSessionInvalidError = (error) => {
    if (!error) return false
    const message = error.message || ''
    const code = error.code || ''
    return (
      code === 'session_not_found' ||
      message.includes('session_not_found') ||
      message.includes('Session from session_id claim in JWT does not exist') ||
      message.includes('Invalid Refresh Token') ||
      message.includes('Refresh Token Not Found')
    )
  }

  // Force clear session state (used when session is invalid)
  const forceLogout = () => {
    setUser(null)
    setSession(null)
    
    // Clear React Query cache to prevent stale data when switching accounts
    // Cancel any in-flight queries first, then clear all cached data
    queryClient.cancelQueries()
    queryClient.clear()
    
    if (typeof window !== 'undefined') {
      [localStorage, sessionStorage].forEach(storage => {
        const keys = Object.keys(storage)
        keys.forEach(key => {
          if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
            storage.removeItem(key)
          }
        })
      })
    }
  }

  useEffect(() => {
    const getSession = async () => {
      if (!isSupabaseConfigured) {
        console.warn('Supabase not configured - running in demo mode')
        setLoading(false)
        return
      }

      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession()
        
        // Check for session_not_found error
        if (error && isSessionInvalidError(error)) {
          console.warn('Session invalid, forcing logout:', error.message)
          forceLogout()
          setLoading(false)
          return
        }
        
        setUser(currentSession?.user || null)
        setSession(currentSession)
        setLoading(false)
      } catch (error) {
        console.warn('Auth session error:', error)
        // If session check fails with invalid session error, force logout
        if (isSessionInvalidError(error)) {
          forceLogout()
        }
        setLoading(false)
      }
    }

    getSession()

    if (!isSupabaseConfigured) {
      return
    }

    try {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, currentSession) => {
          // Handle token refresh errors
          if (event === 'TOKEN_REFRESHED' && !currentSession) {
            console.warn('Token refresh failed, forcing logout')
            forceLogout()
            return
          }
          
          // Handle sign out event
          if (event === 'SIGNED_OUT') {
            forceLogout()
            return
          }
          
          setUser(currentSession?.user || null)
          setSession(currentSession)
          setLoading(false)
        }
      )

      return () => subscription?.unsubscribe()
    } catch (error) {
      console.warn('Auth state change error:', error)
      if (isSessionInvalidError(error)) {
        forceLogout()
      }
    }
  }, [])

  const signUp = async (email, password, metadata = {}, captchaToken = null) => {
    if (!isSupabaseConfigured) {
      return { data: null, error: { message: 'Authentication not configured for development mode' } }
    }
    
    try {
      const signUpOptions = {
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location.origin}/verification-success`
        }
      }

      // Add captcha token if provided
      if (captchaToken) {
        signUpOptions.options.captchaToken = captchaToken
      }

      const { data, error } = await supabase.auth.signUp(signUpOptions)
      
      // Check if user already exists (Supabase returns fake user object for existing emails)
      if (data?.user && (!data.user.identities || data.user.identities.length === 0)) {
        return { 
          data: null, 
          error: { 
            message: 'This email address is not available. Please sign in, reset your password, or contact support if you need help.' 
          } 
        }
      }
      
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  const signIn = async (email, password, captchaToken = null, rememberMe = true) => {
    if (!isSupabaseConfigured) {
      return { data: null, error: { message: 'Authentication not configured for development mode' } }
    }
    
    try {
      setRememberMe(rememberMe)

      const signInOptions = {
        email,
        password
      }

      if (captchaToken) {
        signInOptions.options = {
          captchaToken
        }
      }

      const { data, error } = await supabase.auth.signInWithPassword(signInOptions)
      
      // Provide user-friendly error messages for common authentication failures
      if (error) {
        let friendlyMessage = error.message
        
        if (error.message?.includes('Email not confirmed')) {
          friendlyMessage = 'Please check your email and click the verification link before signing in.'
        } else if (error.message?.includes('Invalid login credentials') || 
            error.message?.includes('invalid_grant')) {
          friendlyMessage = 'The email or password you entered is incorrect. Please double-check and try again.'
        } else if (error.message?.includes('Too many requests')) {
          friendlyMessage = 'Too many sign-in attempts. Please wait a few minutes before trying again.'
        }
        
        return { 
          data, 
          error: { 
            ...error, 
            message: friendlyMessage 
          } 
        }
      }
      
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  const signOut = async () => {
    trackSignOut()
    forceLogout()
    
    if (!isSupabaseConfigured) {
      return { error: null }
    }
    
    try {
      const { error } = await supabase.auth.signOut()
      // Ignore session-related errors - session was already invalid on server
      if (error && !isSessionInvalidError(error) && error.status !== 403) {
        console.warn('Sign out warning:', error)
      }
      return { error: null }
    } catch (error) {
      // Ignore errors during sign out - local state is already cleared
      console.warn('Sign out error (ignored):', error)
      return { error: null }
    }
  }

  const resetPassword = async (email, captchaToken = null) => {
    if (!isSupabaseConfigured) {
      return { data: null, error: { message: 'Authentication not configured for development mode' } }
    }
    
    try {
      const resetOptions = {
        redirectTo: `${window.location.origin}/reset-password`
      }

      // Add captcha token if provided
      if (captchaToken) {
        resetOptions.captchaToken = captchaToken
      }

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, resetOptions)
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  const updatePassword = async (password, captchaToken = null) => {
    if (!isSupabaseConfigured) {
      return { data: null, error: { message: 'Authentication not configured for development mode' } }
    }
    
    try {
      const updateOptions = {
        password
      }

      // Add captcha token if provided
      if (captchaToken) {
        updateOptions.captchaToken = captchaToken
      }

      const { data, error } = await supabase.auth.updateUser(updateOptions)
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  const resendVerificationEmail = async (email, captchaToken = null) => {
    if (!isSupabaseConfigured) {
      return { data: null, error: { message: 'Authentication not configured for development mode' } }
    }
    
    try {
      const resendOptions = {
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/verification-success`
        }
      }

      // Add captcha token if provided
      if (captchaToken) {
        resendOptions.options.captchaToken = captchaToken
      }

      const { data, error } = await supabase.auth.resend(resendOptions)
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    forceLogout,
    resetPassword,
    updatePassword,
    resendVerificationEmail,
    getAuthToken: () => session?.access_token,
    isSupabaseConfigured
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
